/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useMemo, useState, useEffect } from "react";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import dayjs from "dayjs";
import {
  Box,
  Breadcrumbs,
  FormControl,
  IconButton,
  InputAdornment,
  Select,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ClearIcon from '@mui/icons-material/Clear';
import { useParams } from "react-router-dom";
import { FilterContainer } from "../../components/filter_container";
import DropdownSearchBar from "../../components/dorpdown_searchbar";
import { SelectedMenuItem } from "../../components/selected_menu_item";
import { UpdateButton } from "../../components/update_button";
import {
  selectBaseStyles,
  viewTypographyStyles,
  datePickerStyles,
} from "../reports/styles";
import {
  StyledBreadCrumbContainer,
  StyledInputLabel,
  StyledLink,
  StyledMenuItem,
} from "../reports/employee_visibility/styles";
import {
  StyledGuestCenterColumn,
  StyledGuestDetailsPage,
  StyledSection,
  StyledGuestDashboardLayout,
  StyledGuestTabList,
  StyledTab,
} from "./styles";
import api from "../../api";
import authenticatedFetch from "../../api/authenticatedFetch";
import { Query_Keys } from "../../lib/queryKeys";
import { useQuery } from "@tanstack/react-query";
import { useNotificationContext } from "../../contexts/notificationContext";
import { GuestPersonalDetail } from "../../components/guest_personal_details";
import { GuestAssociatedRestaurants } from "../../components/associated_guest_restaurant";
import { GuestOrderVisits } from "../../components/guest_order_and_visit";
import { GuestTopRestaurants } from "../../components/guest_top_restaurants";
import { GuestActivityLog } from "../../components/guest_activity_log";
import { CACHE_DURATION } from "../../constants/cache_duration";
import TabContext from '@mui/lab/TabContext';
import TabPanel from "@mui/lab/TabPanel";
import { IndexTable } from "../../components/index_table";
import camelize from "../../utils/camelize";
import { formatDate } from "../../utils/formatDate";
import { Loader } from "../../components/loader";
import { buildQueryParams } from "../../utils/buildQueryParams";
import { containsText } from "../../utils/normalizeSearchTerm";
import { OrderAppliedDiscountModal } from "../../components/order_discount_modal";
import { StatusContainer } from "../reports/employee_discount_card_summary/styles";
import { EmployeeCardAllocationLogsModal } from "../../components/employee_card_allocation_logs_modal";
import { OrderNumberCell } from "../../components/order_number_cell";

const ALL_VALUE = 'all';

const DATE_OPTIONS = [
  'All Time',
  'Today',
  'Yesterday',
  'This Week',
  'Last 7 Days',
  'This Month',
  'Last Month',
  'Custom Date',
];

const ORDER_HISTORY_TAB = 'order-history';
const CARDS_TAB = 'cards';
const SPEND_BREAKDOWN_TAB = 'spend-breakdown';
const ACTIVITY_LOG_TAB = 'activity-log';
const DEFAULT_ITEMS_PER_PAGE = 25;
const DEFAULT_SORT_BY = 'order_id';
const DEFAULT_SORT_ORDER = 'desc';
const DEFAULT_CARDS_SORT_BY = 'action_date';
const CARDS_SORT_ORDER = 'desc';
const DEFAULT_SPEND_BREAKDOWN_SORT_BY = 'restaurant_name';
const DEFAULT_SPEND_BREAKDOWN_SORT_ORDER = 'asc';
const EMPTY_DURATION = '—'; 
const ACTIVITY_LOG_DEFAULT_DATE_FILTER = 'Last 7 Days';
const ACTIVITY_LOG_MAX_DAYS = 90;
const ACTIVITY_LOG_DATE_RANGE_ERROR = 'Please select a valid date range (max 90 days)';
const ALL_ORDER_MATCH_TYPES = 'all';
const ORDER_MATCH_TYPES = Object.freeze({
  CHECK_IN: 'check_in',
  KNOWN_GUEST: 'known_guest',
});

const ORDER_MATCH_TYPE_OPTIONS = [
  { value: ALL_ORDER_MATCH_TYPES, label: 'All Match Types' },
  { value: ORDER_MATCH_TYPES.CHECK_IN, label: 'Check-In' },
  { value: ORDER_MATCH_TYPES.KNOWN_GUEST, label: 'Known Guest' },
];

const buildDefaultFilters = (id) => ({
  dateFilter: 'All Time',
  restaurant_ids: [ALL_VALUE],
  order_match_type: ALL_ORDER_MATCH_TYPES,
  customDateFrom: null,
  customDateTo: null,
  guest_id: id || null,
});

const getRestaurantDisplayValue = (restaurantIds, restaurantList) => {
  if (!restaurantIds?.length || (restaurantIds.length === 1 && restaurantIds.includes(ALL_VALUE))) {
    return 'All Restaurants';
  }

  if (restaurantIds.length === 1) {
    const restaurant = restaurantList.find((item) => item.id === restaurantIds[0]);
    return restaurant?.name || 'All Restaurants';
  }

  return `${restaurantIds.length} Restaurants Selected`;
};

const buildDateRange = (selectedFilters, currentDate = dayjs()) => {
  if (selectedFilters?.dateFilter !== 'Custom Date') {
    switch (selectedFilters?.dateFilter) {
      case 'Today':
        return {
          from: currentDate,
          to: currentDate,
        };
      case 'Last 7 Days':
        return {
          from: currentDate.subtract(7, 'day'),
          to: currentDate,
        };
      case 'Yesterday':
        return {
          from: currentDate.subtract(1, 'day'),
          to: currentDate.subtract(1, 'day'),
        };
      case 'This Week':
        return {
          from: currentDate.startOf('week'),
          to: currentDate.endOf('week'),
        };
      case 'This Month':
        return {
          from: currentDate.startOf('month'),
          to: currentDate.endOf('month'),
        };
      case 'Last Month':
        return {
          from: currentDate.subtract(1, 'month').startOf('month'),
          to: currentDate.subtract(1, 'month').endOf('month'),
        };
      default:
        return null;
    }
  }

  if (selectedFilters?.customDateFrom && selectedFilters?.customDateTo) {
    return {
      from: dayjs(selectedFilters.customDateFrom),
      to: dayjs(selectedFilters.customDateTo),
    };
  }

  return null;
};

const formatDateRange = (dateRange) => {
  if (!dateRange) {
    return null;
  }

  return {
    from: dateRange.from.format('DD/MM/YYYY'),
    to: dateRange.to.format('DD/MM/YYYY'),
  };
};

const buildActivityLogDateFilters = (currentFilters) => ({
  ...currentFilters,
  dateFilter: ACTIVITY_LOG_DEFAULT_DATE_FILTER,
  customDateFrom: null,
  customDateTo: null,
});

const normalizeFilterDate = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate.valueOf() : null;
};

const areFiltersEqual = (leftFilters, rightFilters) => {
  const leftRestaurantIds = leftFilters?.restaurant_ids || [];
  const rightRestaurantIds = rightFilters?.restaurant_ids || [];

  return (
    leftFilters?.dateFilter === rightFilters?.dateFilter &&
    leftFilters?.guest_id === rightFilters?.guest_id &&
    normalizeFilterDate(leftFilters?.customDateFrom) === normalizeFilterDate(rightFilters?.customDateFrom) &&
    normalizeFilterDate(leftFilters?.customDateTo) === normalizeFilterDate(rightFilters?.customDateTo) &&
    leftRestaurantIds.length === rightRestaurantIds.length &&
    leftRestaurantIds.every((value, index) => value === rightRestaurantIds[index])
  );
};

const isActivityLogDateRangeValid = (selectedFilters) => {
  const dateRange = buildDateRange(selectedFilters);

  if (!dateRange) {
    return false;
  }

  const daysBetween = dateRange.to.startOf('day').diff(dateRange.from.startOf('day'), 'day');
  return daysBetween >= 0 && daysBetween <= ACTIVITY_LOG_MAX_DAYS;
};

const getOrderMatchTypeDisplayValue = (orderMatchType) => {
  const selectedOption = ORDER_MATCH_TYPE_OPTIONS.find((option) => option.value === orderMatchType);
  return selectedOption?.label || ORDER_MATCH_TYPE_OPTIONS[0].label;
};

export const GuestDetailedView = () => {
  const { id } = useParams();
  const showNotification = useNotificationContext();

  const [restaurantSearch, setRestaurantSearch] = useState('');
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [filters, setFilters] = useState(() => buildDefaultFilters(id));
  const [appliedFilters, setAppliedFilters] = useState(() => buildDefaultFilters(id));
  const [restaurantList, setRestaurantList] = useState([])
  const [tab, setTab] = useState(ORDER_HISTORY_TAB);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE)
  const [page, setPage] = useState(1)
  const [csvLoading, setCsvLoading] = useState(false)
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY)
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER)
  const [cardItemsPerPage, setCardItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE)
  const [cardPage, setCardPage] = useState(1)
  const [cardsCsvLoading, setCardsCsvLoading] = useState(false)
  const [spendBreakdownCsvLoading, setSpendBreakdownCsvLoading] = useState(false)
  const [cardSortBy, setCardSortBy] = useState(DEFAULT_CARDS_SORT_BY)
  const [cardSortOrder, setCardSortOrder] = useState(CARDS_SORT_ORDER)
  const [spendBreakdownSortBy, setSpendBreakdownSortBy] = useState(DEFAULT_SPEND_BREAKDOWN_SORT_BY)
  const [spendBreakdownSortOrder, setSpendBreakdownSortOrder] = useState(DEFAULT_SPEND_BREAKDOWN_SORT_ORDER)
  const [orderOpen, setOrderOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState('')
  const [cardLogOpen, setCardLogOpen] = useState(false)
  const [selectedGuestCard, setSelectedGuestCard] = useState(null)

  useEffect(() => {
    const nextFilters = buildDefaultFilters(id);
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setFiltersChanged(false);
    setTab(ORDER_HISTORY_TAB);
    setItemsPerPage(DEFAULT_ITEMS_PER_PAGE);
    setPage(1);
    setSortBy(DEFAULT_SORT_BY);
    setSortOrder(DEFAULT_SORT_ORDER);
    setCardItemsPerPage(DEFAULT_ITEMS_PER_PAGE);
    setCardPage(1);
    setCardSortBy(DEFAULT_CARDS_SORT_BY);
    setCardSortOrder(CARDS_SORT_ORDER);
    setSpendBreakdownSortBy(DEFAULT_SPEND_BREAKDOWN_SORT_BY);
    setSpendBreakdownSortOrder(DEFAULT_SPEND_BREAKDOWN_SORT_ORDER);
  }, [id]);

  const fetchGuestPersonalDetails = async () => {
    const response = await api.get(`/v1/admin/guest_search/${id}`);
    return response?.data;
  };

  const { data: personalDetail, isFetching: personalDetailFetching, isError: personalDetailError } = useQuery({
    queryKey: Query_Keys.guest_details({
      guest_id: id,
    }),
    queryFn: fetchGuestPersonalDetails,
    staleTime: CACHE_DURATION,
    enabled: Boolean(id),
  });

  const fetchGuestAssociatedRestaurants = async () => {
    const response = await api.get( `/v1/admin/guest_search/${id}/associated_guest_restaurants`)
    return response?.data
  }

  const { data: associatedRestaurants, isFetching: associatedRestaurantFetching  , isError: associatedRestaurantsError } = useQuery({
    queryKey: Query_Keys.associated_guest_restaurants({
      guest_id: id,
    }),
    queryFn: fetchGuestAssociatedRestaurants,
    staleTime: CACHE_DURATION,
    enabled: Boolean(id),
  })

  useEffect(() => {
    const data = associatedRestaurants?.association_data;

    if (!data?.length) { setRestaurantList([]); return; }
    setRestaurantList(data.map((res) => ({ id: res?.restaurant_id, name: res?.restaurant_name })));
  }, [associatedRestaurants])

  const pageGuestName = personalDetail?.name || 'Guest Details';

  const filteredRestaurants = useMemo(() => {
    return restaurantList?.filter((restaurant) => containsText(restaurant?.name || '', restaurantSearch));
  }, [restaurantSearch, restaurantList]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setFiltersChanged(true);
  };

  const handleRestaurantChange = (event) => {
    const {
      target: { value },
    } = event;

    if ((value?.length === 1 && value.includes(ALL_VALUE)) || value?.length === 0) {
      setFilters((prev) => ({ ...prev, restaurant_ids: [ALL_VALUE] }));
    } else {
      setFilters((prev) => ({
        ...prev,
        restaurant_ids: value.filter((item) => item !== ALL_VALUE),
      }));
    }

    setFiltersChanged(true);
  };

  const handleRestaurantClear = () => {
    setFilters((prev) => ({ ...prev, restaurant_ids: [ALL_VALUE] }));
    setFiltersChanged(true);
  };

  const handleUpdate = () => {
    if (tab === ACTIVITY_LOG_TAB && !isActivityLogDateRangeValid(filters)) {
      showNotification(ACTIVITY_LOG_DATE_RANGE_ERROR, 'error');
      return;
    }

    setAppliedFilters(filters);
    setFiltersChanged(false);
    setPage(1);
    setCardPage(1);
  };

   const handleChange = (_event, newValue) => {
    if (newValue === ACTIVITY_LOG_TAB && tab !== ACTIVITY_LOG_TAB) {
      const nextFilters = buildActivityLogDateFilters(filters);
      const nextAppliedFilters = buildActivityLogDateFilters(appliedFilters);

      setFilters(nextFilters);
      setAppliedFilters(nextAppliedFilters);
      setFiltersChanged(!areFiltersEqual(nextFilters, nextAppliedFilters));
    }

    setTab(newValue)
  }

  const handleOrderCsvExport = async () => {
    try {
      setCsvLoading(true);
      const query = buildQueryParams(formattedOrderFilters)
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=orders&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date()
      let filename = `order_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  };

  const handleCardsCsvExport = async () => {
    try {
      setCardsCsvLoading(true);
      const query = buildQueryParams(formattedFilters)
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=network_guest_discounts_summary&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      const date = new Date()
      const filename = `guest_cards_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCardsCsvLoading(false);
    }
  };

  const formatFilters = useCallback(() => {
    const params = {};
    const dateRange = formatDateRange(buildDateRange(appliedFilters));

    if (dateRange) {
      params.date_range = dateRange;
    }

    if (appliedFilters?.restaurant_ids.length > 0 && !appliedFilters?.restaurant_ids.includes(ALL_VALUE)) {
      params.restaurant_ids = appliedFilters?.restaurant_ids
    }

    params.network_guest_ids = id == null ? [] : [Number(id)]

    return params
  }, [appliedFilters, id])

  const formattedFilters = useMemo(() => formatFilters(), [formatFilters]);

  const formattedOrderFilters = useMemo(() => {
    if (appliedFilters?.order_match_type === ALL_ORDER_MATCH_TYPES) {
      return formattedFilters;
    }

    return {
      ...formattedFilters,
      order_match_type: appliedFilters?.order_match_type,
    };
  }, [appliedFilters?.order_match_type, formattedFilters]);

  const guestOrderVisitFilters = useMemo(() => {
    const params = {};

    if (formattedFilters?.date_range) {
      params.date_range = formattedFilters.date_range;
    }

    if (formattedFilters?.restaurant_ids) {
      params.restaurant_ids = formattedFilters.restaurant_ids;
    }

    return params;
  }, [formattedFilters]);

  const handleSpendBreakdownCsvExport = useCallback(async () => {
    try {
      setSpendBreakdownCsvLoading(true);

      const query = buildQueryParams({
        ...guestOrderVisitFilters,
        network_guest_id: Number(id),
        sort_by: spendBreakdownSortBy,
        sort_order: spendBreakdownSortOrder,
      });
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=guest_spend_breakdown&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      const date = new Date()
      const filename = `guest_spend_breakdown_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setSpendBreakdownCsvLoading(false);
    }
  }, [guestOrderVisitFilters, id, spendBreakdownSortBy, spendBreakdownSortOrder]);

  const fetchGuestOrderVisits = useCallback(async () => {
    const response = await api.get(`/v1/admin/guest_search/${id}/visits_orders_calculation`, {
      params: guestOrderVisitFilters,
    })

    return response?.data
  }, [guestOrderVisitFilters, id])

  const {
    data: orderAndGuestData,
    isFetching: orderAndGuestDataFetching,
    isError: orderAndGuestDataError,
    refetch: refetchOrderAndGuestData,
  } = useQuery({
    queryKey: Query_Keys.order_and_visits({
      guest_id: id,
      filters: guestOrderVisitFilters,
    }),
    queryFn: fetchGuestOrderVisits,
    staleTime: CACHE_DURATION,
    enabled: Boolean(id),
  })

  const fetchGuestTopRestaurants = useCallback(async () => {
    const response = await api.get(`/v1/admin/guest_search/${id}/top_guest_visits`)

    return response?.data
  }, [id])

  const {
    data: guestTopRestaurants,
    isFetching: guestTopRestaurantsFetching,
    isError: guestTopRestaurantsError,
    refetch: refetchGuestTopRestaurants,
  } = useQuery({
    queryKey: Query_Keys.top_restaurants({
      guest_id: id,
    }),
    queryFn: fetchGuestTopRestaurants,
    staleTime: CACHE_DURATION,
    enabled: Boolean(id),
  })

  const fetchGuestOrders = useCallback(async () => {
    const queryParams = {
      ...formattedOrderFilters,
      per_page: itemsPerPage,
      page: page,
      sort_by: sortBy,
      sort_order: sortOrder,
    };

    const response = await api.get('/v1/admin/orders', { params: queryParams })

    return {data: response?.data, pageCount: response?.headers["total-count"]}
  }, [formattedOrderFilters, itemsPerPage, page, sortBy, sortOrder])

  const { data: orderData, isFetching: orderFetching, isError: orderError } = useQuery({
    queryKey: Query_Keys.guest_orders({
      guest_id: id,
      filters: formattedOrderFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchGuestOrders,
    staleTime: CACHE_DURATION,
    enabled: Boolean(id),
  })

  const fetchGuestCards = useCallback(async () => {
    const queryParams = {
      ...formattedFilters,
      per_page: cardItemsPerPage,
      page: cardPage,
    };

    if (cardSortBy) {
      queryParams.sort_by = cardSortBy;
      queryParams.sort_order = cardSortOrder;
    }

    const response = await api.get('/v1/admin/reports/network_guest_discounts', {
      params: queryParams,
    })

    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }, [formattedFilters, cardItemsPerPage, cardPage, cardSortBy, cardSortOrder])

  const {
    data: cardsData,
    isFetching: cardsFetching,
    isError: cardsError,
  } = useQuery({
    queryKey: Query_Keys.employee_discount_report({
      queryFilter: formattedFilters,
      page: cardPage,
      itemsPerPage: cardItemsPerPage,
      sortBy: cardSortBy,
      sortOrder: cardSortOrder,
      network_guest_id: id,
    }),
    queryFn: fetchGuestCards,
    staleTime: CACHE_DURATION,
    enabled: Boolean(id) && tab === CARDS_TAB,
  })

  const fetchGuestSpendBreakdown = useCallback(async () => {
    const queryParams = {
      ...guestOrderVisitFilters,
    };

    if (spendBreakdownSortBy) {
      queryParams.sort_by = spendBreakdownSortBy;
      queryParams.sort_order = spendBreakdownSortOrder;
    }

    const response = await api.get(`/v1/admin/guest_search/${id}/guest_spend_breakdown`, {
      params: queryParams,
    })

    return response?.data
  }, [guestOrderVisitFilters, id, spendBreakdownSortBy, spendBreakdownSortOrder])

  const {
    data: spendBreakdownData,
    isFetching: spendBreakdownFetching,
    isError: spendBreakdownError,
  } = useQuery({
    queryKey: Query_Keys.guest_spend_breakdown({
      filters: guestOrderVisitFilters,
      network_guest_id: id,
      sortBy: spendBreakdownSortBy,
      sortOrder: spendBreakdownSortOrder,
    }),
    queryFn: fetchGuestSpendBreakdown,
    staleTime: CACHE_DURATION,
    enabled: Boolean(id) && tab === SPEND_BREAKDOWN_TAB,
  })

  const fetchGuestActivityLog = useCallback(async () => {
    const response = await api.get(`/v1/admin/guest_search/${id}/activity_log`, {
      params: guestOrderVisitFilters,
    })

    return response?.data
  }, [guestOrderVisitFilters, id])

  const {
    data: guestActivityLogData,
    isFetching: guestActivityLogFetching,
    isError: guestActivityLogError,
  } = useQuery({
    queryKey: Query_Keys.guest_activity_log({
      network_guest_id: id,
      filters: guestOrderVisitFilters,
    }),
    queryFn: fetchGuestActivityLog,
    staleTime: CACHE_DURATION,
    enabled: Boolean(id) && tab === ACTIVITY_LOG_TAB,
  })

  const fetchOrderAppliedDiscount = useCallback(async () => {
    if (!selectedOrder?.order_id) {
      return [];
    }
    else {
      const response = await api.get(
        `/v1/admin/orders/${selectedOrder?.order_id}/detail`,
      );
      return response?.data;
    }
  }, [selectedOrder]);

  const { data: orderDetails, isFetching: orderDetailsFetching, isError: orderDetailsError } = useQuery({
    queryKey: Query_Keys.applied_discount({
      selected_order: selectedOrder?.order_id,
    }),
    queryFn: fetchOrderAppliedDiscount,
    staleTime: CACHE_DURATION,
    enabled: selectedOrder !== "",
  });

  useEffect(() => {
    if (personalDetailError) {
      showNotification('Failed to fetch personal details', 'error');
    }

    if (associatedRestaurantsError) {
      showNotification('Failed to fetch associated restaurant details', 'error');
    }

    if (orderError) {
      showNotification('Failed to fetch order details', 'error');
    }

    if (orderAndGuestDataError) {
      showNotification('Failed to fetch order and visit details', 'error');
    }

    if (cardsError) {
      showNotification('Failed to fetch card details', 'error');
    }

    if (spendBreakdownError) {
      showNotification('Failed to fetch spend breakdown details', 'error');
    }

    if (guestActivityLogError) {
      showNotification('Failed to fetch activity log details', 'error');
    }

    if (guestTopRestaurantsError) {
      showNotification('Failed to fetch top restaurants', 'error');
    }

    if (orderDetailsError) {
      showNotification('Failed to fetch order applied discount details', 'error');
    }
  }, [
    personalDetailError,
    showNotification,
    associatedRestaurantsError,
    orderError,
    orderAndGuestDataError,
    cardsError,
    spendBreakdownError,
    guestActivityLogError,
    guestTopRestaurantsError,
    orderDetailsError,
  ]);

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy)
    setSortOrder(nextSortOrder)
    setPage(1)
  }

  const handleCardSortChange = (nextSortBy, nextSortOrder) => {
    setCardSortBy(nextSortBy)
    setCardSortOrder(nextSortOrder)
    setCardPage(1)
  }

  const handleSpendBreakdownSortChange = (nextSortBy, nextSortOrder) => {
    setSpendBreakdownSortBy(nextSortBy)
    setSpendBreakdownSortOrder(nextSortOrder)
  }

  const columnOptions = useMemo(() => [
    { key: "location", sortable: true },
    { key: "order_id", sortable: true },
    { key: "order_no", label: 'Order #', sortable: true },
    { key: 'order_details', label: 'Order Details', sortable: false },
    { key: "opened", sortable: true },
    { key: "no_of_guest", label: '# of Guests', sortable: true },
    { key: "discount_amount", sortable: true },
    { key: "amount", sortable: true },
    { key: "tax", sortable: true },
    { key: "tip", sortable: true },
    { key: "gratuity", sortable: true },
    { key: "total", sortable: true },
    { key: "paid", sortable: true },
    { key: "closed", sortable: true },
    { key: "duration", sortable: true },
    { key: "check_no", label: 'Checks', sortable: true },
    { key: "tab_names", sortable: true },
  ], []);

  const columns = useMemo(() => {
    return columnOptions?.map((column) => {
      return {
        key: column.key,
        label: column.label || camelize(column.key),
        sortable: column.sortable,
      };
    });
  }, [columnOptions]);

  const formatAmount = useCallback((amount) => {
    return amount == null ? '0.00' : Number(amount).toFixed(2)
  }, [])

  const formatTime = useCallback((seconds) => {
    if (seconds == null) {
      return EMPTY_DURATION
    }

    if (seconds >= 0) {
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;

      return [
        hrs.toString().padStart(2, "0"),
        mins.toString().padStart(2, "0"),
        secs.toString().padStart(2, "0"),
      ].join(":");
    }

    return EMPTY_DURATION
  }, [])

  const handleOrderOpen = useCallback((order) => {
    setOrderOpen(true)
    setSelectedOrder(order)
  }, [])

  const handleOrderClose = () => {
    setOrderOpen(false);
    setSelectedOrder('')
  };

  const handleCardLogOpen = useCallback(() => {
    setCardLogOpen(true)
  }, [])

  const handleCardLogClose = useCallback(() => {
    setCardLogOpen(false)
    setSelectedGuestCard(null)
  }, [])

  const handleSelectedGuestCard = useCallback((row) => {
    setSelectedGuestCard(row?.sourceRecord ?? null)
  }, [])

  const buildTableRows = useCallback((data) => {
    return data?.map((record) => {
      return {
        location: record?.location,
        order_id: record?.order_id,
        order_no: record?.order_no,
        order_details: (
          <OrderNumberCell
            orderValue={record}
            onView={handleOrderOpen}
          />
        ),
        opened: formatDate(record?.opened, record?.time_zone_abbr),
        no_of_guest: record?.no_of_guest,
        discount_amount: `$${formatAmount(record?.discount_amount)}`,
        amount: `$${formatAmount(record?.amount)}`,
        tax: `$${formatAmount(record?.tax)}`,
        tip: `$${formatAmount(record?.tip)}`,
        gratuity: `$${formatAmount(record?.gratuity)}`,
        total: `$${formatAmount(record?.total)}`,
        paid: formatDate(record?.paid, record?.time_zone_abbr),
        closed: formatDate(record?.closed, record?.time_zone_abbr),
        duration: formatTime(record?.duration),
        check_no: record?.check_no,
        tab_names: record?.tab_names
      };
    });
  }, [formatAmount, formatTime, handleOrderOpen]);

  const rows = useMemo(() => buildTableRows(orderData?.data || []), [buildTableRows, orderData?.data]);

  const cardColumnOptions = useMemo(() => [
    { key: 'employee_phone', sortable: true },
    { key: 'action_date', sortable: true },
    { key: 'name', sortable: true },
    { key: 'restaurant_group', sortable: true },
    { key: 'restaurant_sub_group', sortable: true },
    { key: 'restaurant', sortable: true },
    { key: 'card_type', label: 'Card Name', sortable: true },
    { key: 'discount_card_type', label: 'Card Type', sortable: true },
    { key: 'status', sortable: true },
    { key: 'last_performed_by', sortable: true },
  ], []);

  const cardColumns = useMemo(() => {
    return cardColumnOptions.map((column) => ({
      key: column.key,
      label: column.label || camelize(column.key),
      sortable: column.sortable,
    }));
  }, [cardColumnOptions]);

  const buildCardTableRows = useCallback((data) => {
    return data?.map((record) => {
      return {
        sourceRecord: record,
        employee_phone: record?.network_guest?.phone_no,
        action_date: formatDate(record?.action_date, record?.time_zone_abbr),
        name: record?.network_guest?.full_name,
        restaurant_group: record?.restaurant_group?.name,
        restaurant_sub_group: record?.restaurant_sub_group?.name || '-',
        restaurant: record?.restaurant?.name || '-',
        card_type: record?.discount_card?.name,
        discount_card_type: camelize(record?.discount_card?.card_type) || '-',
        status: {
          display: (
            <StatusContainer status={record?.status}>
              {camelize(record?.status)}
            </StatusContainer>
          ),
          sortValue: record?.status,
        },
        last_performed_by: record?.last_performed_by || '-',
      };
    });
  }, []);

  const cardRows = useMemo(() => buildCardTableRows(cardsData?.data || []), [buildCardTableRows, cardsData?.data]);

  const spendBreakdownColumnOptions = useMemo(() => [
    { key: 'restaurant_name', label: 'Restaurant', sortable: true },
    { key: 'named_guest_visit_count', label: 'Named Visits', sortable: true },
    { key: 'check_in_visit_count', label: 'Check-ins Visits', sortable: true },
    { key: 'avg_check', label: 'Avg Check', sortable: true },
    { key: 'total_tip', label: 'Total Tip', sortable: true },
    { key: 'total_discount', label: 'Total Discount', sortable: true },
    { key: 'total_spend', label: 'Total Spend', sortable: true },
    { key: 'first', label: 'First', sortable: true },
    { key: 'last', label: 'Last', sortable: true },
  ], []);

  const spendBreakdownColumns = useMemo(() => {
    return spendBreakdownColumnOptions.map((column) => ({
      key: column.key,
      label: column.label,
      sortable: column.sortable,
    }));
  }, [spendBreakdownColumnOptions]);

  const formatBusinessDate = useCallback((date) => {
    if (!date) {
      return '-'
    }

    const parsedDate = dayjs(date)

    return parsedDate.isValid() ? parsedDate.format('MM/DD/YYYY') : '-'
  }, [])

  const buildSpendBreakdownRows = useCallback((data) => {
    return data?.map((record) => {
      return {
        restaurant_name: record?.restaurant_name,
        named_guest_visit_count: record?.named_guest_visit_count,
        check_in_visit_count: record?.check_in_visit_count,
        avg_check: `$${formatAmount(record?.avg_check)}`,
        total_tip: `$${formatAmount(record?.total_tip)}`,
        total_discount: `$${formatAmount(record?.total_discount)}`,
        total_spend: `$${formatAmount(record?.total_spend)}`,
        first: formatBusinessDate(record?.first),
        last: formatBusinessDate(record?.last),
      };
    });
  }, [formatAmount, formatBusinessDate]);

  const spendBreakdownRows = useMemo(
    () => buildSpendBreakdownRows(spendBreakdownData || []),
    [buildSpendBreakdownRows, spendBreakdownData]
  );

  const spendBreakdownPageCount = spendBreakdownRows?.length || 0;
  const spendBreakdownItemsPerPage = spendBreakdownPageCount || 1;

  return (
    <StyledGuestDetailsPage>
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
          <StyledLink color="#515152" component="span">Employee 360</StyledLink>
          <StyledLink color="#515152" component="span">{pageGuestName}</StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <FilterContainer>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: { xs: 2, md: 0 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: '18px' },
              flexWrap: 'wrap',
              width: { xs: '100%', md: 'auto' },
              flex: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                ...viewTypographyStyles,
                alignSelf: { xs: 'flex-start', sm: 'center' },
                whiteSpace: 'nowrap',
              }}
            >
              View
            </Typography>

            <FormControl
              size="small"
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: '100%', sm: '220px' },
              }}
            >
              <StyledInputLabel>Restaurant</StyledInputLabel>
              <Select
                multiple
                size="small"
                label="Restaurant"
                data-testid="guest-detail-restaurant-filter"
                value={filters?.restaurant_ids || [ALL_VALUE]}
                onChange={handleRestaurantChange}
                onClose={() => setTimeout(() => setRestaurantSearch(''), 300)} // This is done to clear the search-bar when drop down is closed
                renderValue={() => getRestaurantDisplayValue(filters?.restaurant_ids, restaurantList)}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  PaperProps: { sx: { maxHeight: '40vh' } },
                  autoFocus: false,
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: '100%', sm: '220px' },
                  borderColor: '#2b4fb9',
                }}
                endAdornment={
                  !filters?.restaurant_ids?.includes(ALL_VALUE) ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton edge="end" size="small" disableRipple onClick={handleRestaurantClear}>
                        <ClearIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              >
                <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder="Search restaurant" />
                {/* All value */}
                <StyledMenuItem
                  key={ALL_VALUE}
                  value={ALL_VALUE}
                  disabled={!filters?.restaurant_ids?.includes(ALL_VALUE)}
                >
                  {filters?.restaurant_ids?.includes(ALL_VALUE) ? <SelectedMenuItem data="All Restaurants" /> : 'All Restaurants'}
                </StyledMenuItem>

                {filteredRestaurants?.map((restaurant) => (
                  <StyledMenuItem key={restaurant?.id} value={restaurant?.id}>
                    {filters?.restaurant_ids?.includes(restaurant?.id)
                      ? <SelectedMenuItem data={restaurant?.name} />
                      : restaurant?.name}
                  </StyledMenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: '100%', sm: '180px' },
              }}
            >
              <StyledInputLabel>Date Range</StyledInputLabel>
              <Select
                value={filters?.dateFilter || ''}
                data-testid="guest-detail-date-filter"
                renderValue={() => filters?.dateFilter}
                label="Date Range"
                size="small"
                onChange={(event) => handleFilterChange('dateFilter', event.target.value)}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  PaperProps: { sx: { width: '230px' } },
                  autoFocus: false,
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: '100%', sm: '180px' },
                  borderColor: '#2b4fb9',
                }}
              >
                {DATE_OPTIONS.map((value) => (
                  <StyledMenuItem value={value} key={value}>
                    {filters?.dateFilter === value ? <SelectedMenuItem data={value} /> : value}
                  </StyledMenuItem>
                ))}
              </Select>
            </FormControl>

            {tab === ORDER_HISTORY_TAB && (
              <FormControl
                size="small"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { xs: '100%', sm: '220px' },
                }}
              >
                <StyledInputLabel>Order Match Type</StyledInputLabel>
                <Select
                  value={filters?.order_match_type || ALL_ORDER_MATCH_TYPES}
                  data-testid="guest-detail-order-match-type-filter"
                  renderValue={() => getOrderMatchTypeDisplayValue(filters?.order_match_type)}
                  label="Order Match Type"
                  size="small"
                  onChange={(event) => handleFilterChange('order_match_type', event.target.value)}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    PaperProps: { sx: { width: '230px' } },
                    autoFocus: false,
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: '100%', sm: '220px' },
                    borderColor: '#2b4fb9',
                  }}
                >
                  {ORDER_MATCH_TYPE_OPTIONS.map((option) => (
                    <StyledMenuItem value={option.value} key={option.value}>
                      {filters?.order_match_type === option.value ? <SelectedMenuItem data={option.label} /> : option.label}
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {filters?.dateFilter === 'Custom Date' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Typography variant="body2" sx={viewTypographyStyles}>
                    Days
                  </Typography>
                  <DatePicker
                    format="MM-DD-YYYY"
                    sx={{
                      ...datePickerStyles,
                      width: { xs: '100%', sm: '150px' },
                    }}
                    slotProps={{ textField: { size: 'small' } }}
                    value={filters?.customDateFrom}
                    onChange={(date) => handleFilterChange('customDateFrom', date)}
                  />
                  <Typography variant="body2" sx={viewTypographyStyles}>
                    through
                  </Typography>
                  <DatePicker
                    format="MM-DD-YYYY"
                    sx={{
                      ...datePickerStyles,
                      width: { xs: '100%', sm: '150px' },
                    }}
                    slotProps={{ textField: { size: 'small' } }}
                    value={filters?.customDateTo}
                    onChange={(date) => handleFilterChange('customDateTo', date)}
                  />
                </LocalizationProvider>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              alignSelf: { xs: 'flex-start', md: 'center' },
              mt: { xs: 1, md: 0 },
              width: { xs: '100%', md: 'auto' },
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            <UpdateButton onClick={handleUpdate} filtersChanged={filtersChanged} status={false} />
          </Box>
        </Box>
      </FilterContainer>

      <StyledGuestDashboardLayout>
        <StyledSection>
          <GuestPersonalDetail
            personalDetail={personalDetail}
            loading={personalDetailFetching}
          />
          <GuestAssociatedRestaurants
            associatedRestaurants={associatedRestaurants}
            isLoading={associatedRestaurantFetching}
          />
        </StyledSection>

        <StyledGuestCenterColumn data-testid="guest-detail-center-column">
          <TabContext value={tab}>
            <Box sx={{ borderBottom: 0, borderColor: 'divider' }}>
              <StyledGuestTabList
                onChange={handleChange}
                aria-label="Guest detail tabs"
                data-testid="guest-detail-tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                <StyledTab
                  label="Order History"
                  value={ORDER_HISTORY_TAB}
                  data-testid="guest-detail-tab-order-history"
                />
                <StyledTab
                  label="Cards"
                  value={CARDS_TAB}
                  data-testid="guest-detail-tab-cards"
                />
                <StyledTab
                  label="Spend Breakdown"
                  value={SPEND_BREAKDOWN_TAB}
                  data-testid="guest-detail-tab-spend-breakdown"
                />
                <StyledTab
                  label="Activity Log"
                  value={ACTIVITY_LOG_TAB}
                  data-testid="guest-detail-tab-activity-log"
                />
              </StyledGuestTabList>

              <TabPanel
                value={ORDER_HISTORY_TAB}
                data-testid="guest-detail-panel-order-history"
                sx={{ px: 0, pb: 0 }}
              >
                {
                  orderFetching ? (
                    <Loader />
                  ) : (
                    <IndexTable
                      columns={columns}
                      data={rows}
                      tableId="order-entries"
                      type="entries"
                      itemsPerPage={itemsPerPage}
                      setItemsPerPage={setItemsPerPage}
                      page={page}
                      setPage={setPage}
                      pageCount={orderData?.pageCount}
                      report={false}
                      showDownloadButton={true}
                      disableDownloadButton={!orderData?.data?.length || orderFetching || csvLoading}
                      handleExportCsv={handleOrderCsvExport}
                      csvLoading={csvLoading}
                      serverSort={true}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSortChange={handleSortChange}
                    />
                  )
                }
              </TabPanel>

              <TabPanel
                value={CARDS_TAB}
                data-testid="guest-detail-panel-cards"
                sx={{ px: 0, pb: 0 }}
              >
                {
                  cardsFetching ? (
                    <Loader />
                  ) : (
                    <IndexTable
                      columns={cardColumns}
                      data={cardRows}
                      tableId="card-entries"
                      type="entries"
                      itemsPerPage={cardItemsPerPage}
                      setItemsPerPage={setCardItemsPerPage}
                      page={cardPage}
                      setPage={setCardPage}
                      pageCount={cardsData?.pageCount}
                      report={false}
                      showDownloadButton={true}
                      disableDownloadButton={!cardsData?.data?.length || cardsFetching || cardsCsvLoading}
                      handleExportCsv={handleCardsCsvExport}
                      csvLoading={cardsCsvLoading}
                      serverSort={true}
                      sortBy={cardSortBy}
                      sortOrder={cardSortOrder}
                      onSortChange={handleCardSortChange}
                      enableRowClick={true}
                      handleOpen={handleCardLogOpen}
                      setSelectedRow={handleSelectedGuestCard}
                    />
                  )
                }
              </TabPanel>

              <TabPanel
                value={SPEND_BREAKDOWN_TAB}
                data-testid="guest-detail-panel-spend-breakdown"
                sx={{ px: 0, pb: 0 }}
              >
                {
                  spendBreakdownFetching ? (
                    <Loader />
                  ) : (
                    <IndexTable
                      columns={spendBreakdownColumns}
                      data={spendBreakdownRows}
                      tableId="spend-breakdown-entries"
                      type="entries"
                      itemsPerPage={spendBreakdownItemsPerPage}
                      setItemsPerPage={() => {}}
                      page={1}
                      setPage={() => {}}
                      pageCount={spendBreakdownPageCount}
                      report={false}
                      hideNavigation={true}
                      showDownloadButton={true}
                      disableDownloadButton={!spendBreakdownRows?.length || spendBreakdownFetching || spendBreakdownCsvLoading}
                      handleExportCsv={handleSpendBreakdownCsvExport}
                      csvLoading={spendBreakdownCsvLoading}
                      serverSort={true}
                      sortBy={spendBreakdownSortBy}
                      sortOrder={spendBreakdownSortOrder}
                      onSortChange={handleSpendBreakdownSortChange}
                    />
                  )
                }
              </TabPanel>
              
              <TabPanel
                value={ACTIVITY_LOG_TAB}
                data-testid="guest-detail-panel-activity-log"
                sx={{ px: 0, pb: 0 }}
              >
                <GuestActivityLog
                  data={guestActivityLogData}
                  isLoading={guestActivityLogFetching}
                />
              </TabPanel>
            </Box>
          </TabContext>
        </StyledGuestCenterColumn>

        <StyledSection>
          <GuestOrderVisits
            orderAndGuestData={orderAndGuestData}
            isLoading={orderAndGuestDataFetching}
            isError={orderAndGuestDataError}
            onRetry={refetchOrderAndGuestData}
          />
          <GuestTopRestaurants
            guestTopRestaurants={guestTopRestaurants}
            isLoading={guestTopRestaurantsFetching}
            isError={guestTopRestaurantsError}
            onRetry={refetchGuestTopRestaurants}
          />
        </StyledSection>
      </StyledGuestDashboardLayout>

      {/* Order Detail Modal */}
      <OrderAppliedDiscountModal
        open={orderOpen}
        handleClose={handleOrderClose}
        ordersData={orderDetails}
        isLoading={orderDetailsFetching}
        isError={orderDetailsError}
      />

      {/* Employee Discount Card Allocation Logs Modal */}
      <EmployeeCardAllocationLogsModal
        open={cardLogOpen}
        handleClose={handleCardLogClose}
        selectedRecord={selectedGuestCard}
      />
    </StyledGuestDetailsPage>
  );
};
